import Vue from 'vue';
import {
  extend,
  ValidationProvider,
  ValidationObserver,
  setInteractionMode,
  localize,
} from 'vee-validate';
import {
  required,
  email,
  min,
  confirmed,
  is,
  numeric,
  regex,
  max,
  max_value,
  integer,
  min_value,
  digits,
} from 'vee-validate/dist/rules';
import en from 'vee-validate/dist/locale/en';
import { parsePhoneNumber } from 'awesome-phonenumber';

Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);

extend('required', { ...required, message: en.messages['required'] });
extend('email', { ...email, message: en.messages['email'] });
extend('phone', {
  validate: value => validatePhone(value),
  message: en.messages['phone'],
});
extend('email_or_phone', {
  validate: value => email.validate(value) || validatePhone(value),
  message: en.messages['email'],
});
extend('cell_number', {
  validate: value => validatePhone(value),
  message: en.messages['phone'],
});
extend('min', { ...min, message: en.messages['min'] });
extend('confirmed', { ...confirmed, message: en.messages['confirmed'] });
extend('is', { ...is, message: en.messages['is'] });
extend('numeric', { ...numeric, message: en.messages['numeric'] });
extend('max', { ...max, message: en.messages['max'] });
extend('regex', { ...regex, message: en.messages['regex'] });
extend('max_value', { ...max_value, message: en.messages['max_value'] });
extend('integer', { ...integer, message: en.messages['integer'] });
extend('min_value', { ...min_value, message: en.messages['min_value'] });
extend('cell_number', {
  message: 'Phone number is invalid.',
});
extend('id_number', {
  validate: value => validateId(value),
  message: 'Invalid South African ID number',
});
extend('age_is_valid', {
  validate: value => isSixteenOrOlder(value),
  message: 'You must be 16 years or older',
});
extend('minimum_age', {
  validate(value, { age }) {
    const birthDate = new Date(value);
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      years--;
    }
    return years >= Number(age);
  },
  params: ['age'],
  message: 'This person must be {age} years or older',
});
extend('id_number_foreign', {
  validate: value => validateId(value),
  message: 'Invalid National ID number',
});
extend('passport_number', {
  validate: value => validateId(value),
  message: 'Invalid Passport number',
});
extend('upload_will_for_version', {
  validate: value => value.status !== 'VALID',
  message: 'Will version already has a valid uploaded will',
}),
  extend('decimal', {
    validate: (value, { decimals = '*', separator = '.' } = {}) => {
      if (value === null || value === undefined || value === '') {
        return {
          valid: false,
        };
      }
      if (Number(decimals) === 0) {
        return {
          valid: /^-?\d*$/.test(value),
        };
      }
      const regexPart = decimals === '*' ? '+' : `{1,${decimals}}`;
      const regex = new RegExp(
        `^[-+]?\\d*(\\${separator}\\d${regexPart})?([eE]{1}[-]?\\d+)?$`
      );
      return {
        valid: regex.test(value),
        data: {
          serverMessage: 'Only decimal values are available',
        },
      };
    },
    message: `{serverMessage}`,
  }),
  extend('phoneIntl', {
    validate(value, args) {
      const regionCode = args[0];
      const pn = parsePhoneNumber(value, { regionCode });
      return pn.valid && pn.regionCode === regionCode;
    },
    message:
      'Phone number is invalid. Ensure the selected country code is correct.',
  }),
  extend('max_currency', {
    validate(value, { max }) {
      value = value.replace(/\s/g, '');
      return value <= max;
    },
    params: ['max'],
    message: 'The {_field_} field must be {max} or lower',
  }),
  extend('min_currency', {
    validate(value, { min }) {
      value = value.replace(/\s/g, '');
      return value >= min;
    },
    params: ['min'],
    message: 'The {_field_} field must be {min} or higher',
  });
extend('password', {
  validate: value => {
    const passwordRegex = new RegExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/);
    return passwordRegex.test(value);
  },
  message: `Minimum 8 characters, at least 1 uppercase, 1 lowercase and 1 number`,
});
extend('emailNotNull', {
  message: 'An email must be provided for the decision maker.',
  validate(value, { email, decisionMakerNotify }) {
    if (decisionMakerNotify) {
      return !!email;
    } else {
      return true;
    }
  },
  params: ['email', 'decisionMakerNotify'],
});
extend('adviserCode', {
  validate: value => /^\d{6}$/.test(value),
  message: 'Adviser Code must be 6 digits',
});

setInteractionMode('eager');

localize({
  en: {
    names: {
      investment_type: 'investment type',
      other_investment_type: 'other investment type',
      physical_address: 'physical address',
      sole_owner: 'sole owner',
      date_of_birth: 'date of birth',
      first_names: 'first names',
      last_name: 'last name',
      charity_name: 'charity name',
      charity_registration_number: 'charity registration number',
      cell_number: 'cell number',
      id_number: 'identity number',
      id_number_foreign: 'identity number foreign',
      age_is_valid: 'age valid',
      passport_number: 'passport number',
      confirm_password: 'confirm password',
      monetary_amount: 'monetary amount',
      total_percentage: 'total percentage',
      children_reference: 'children reference',
      kinship: 'relationship to person',
      voucher_code: 'voucher code',
      marital_regime: 'marital regime',
      burial_preference: 'burial preference',
      trust_termination: 'trust termination age',
      uploaded_will_status: 'uploaded will status',
      advisor_email: 'advisor email',
      cash_on_death: 'cash needed immediately on death',
      monthy_expenses: 'current monthly expenses',
      value_of_estate: 'estimated value of your estate',
      spouse_initial_guardian: 'above',
      default_executor_fee: 'default Executor fee',
      negotiable_executor_fee: 'negotiable Executor fee',
      nominated_executor: 'nominated Executor',
      trading_name: 'trading name',
      estate_admin_expenses_cover_required: 'estate administration expenses',
      total_cover: 'total amount of cover',
      education_level: 'education level',
      asset_value_left_to_spouse: 'asset value left to spouse',
      all_assets_left_to_spouse: 'assets left to spouse',
      non_property_asset_value: 'non property asset value',
      market_value: 'market value',
      total_liabilities: 'total liabilities',
      other_liabilities: 'other liabilities',
      outstanding_debt: 'outstanding debt',
      account_holder: 'account holder',
      account_number: 'account number',
      branch_code: 'branch code',
      id_or_passport_number: 'id or passport number',
      id_type: 'id type',
      country_code: 'nationality',
      debit_order_date: 'debit order date',
      replacement_policy: 'replacement policy',
      send_email: 'send invite email',
      deemed_assets_value: 'deemed assets value',
      include_living_will: 'create living will',
      decision_maker_notify: 'decision maker notify',
      kept_alive_by_artificial_means: 'kept alive by artificial means',
      kept_alive_time_amount: 'kept alive time amount',
      kept_alive_time_period: 'kept alive time period',
      comfort_care: 'comfort care',
      burial_preference_other: 'other burial preference',
      will_payment_option: 'will payment option',
      executor_party: 'Executor party',
      percentage_share: 'percentage share',
      has_estate_duty_rollover: 'estate duty rollover',
      estate_duty_rollover_amount: 'estate duty rollover amount',
      offer_trust_administration: 'offer trust administration',
      trust_company_name: 'trust company name',
      trust_company_reg_no: 'trust company registration number',
      refer_a_friend: 'refer a friend',
      insurance_step: 'insurance step',
      trust_name: 'Trust name',
      trust_incorporation_number: 'Trust incorporation number',
      hide_quickwill_logo_on_will: 'Hide quickwill logo on will',
      transfer_to_advisor: 'advisor to transfer testators to',
      are_you_sure: 'are you sure',
      will_country: 'will country',
      heard_about_us: 'where did you hear about us',
      marital_status: 'marital status',
      living_will_decision_maker: 'living will decision maker',
      is_adult_confirmed: 'age confirmation',
      executor_fee_location: 'estate admin fee location',
      uniqueIdentifier: 'industry number',
      system_wide_access: 'access level',
      adviserCode: 'adviser code',
      monthly_income_value: 'monthly income ',
      include_burial_wishes: 'include burial wishes',
      terms_and_conditions: 'terms and conditions',
      collection_date: 'collection date',
      alternative_cell_number: 'alternative cell number',
      company_name: 'company name',
      google_address: 'address',
      full_name: 'full name',
      street_number: 'street number',
      street_name: 'street name',
      postal_code: 'postal code',
      special_instructions_collection: 'special instructions',
    },
    fields: {
      accepted_terms: {
        is: 'Please read and accept the terms and conditions',
      },
      total_cover: {
        max_value: 'The max total cover you can apply for is R200 000',
      },
      total_cover: {
        min_value: 'The min total cover you can apply for is R50 000',
      },
      living_will_decision_maker: {
        required: 'Please select a person above',
      },
      executor: {
        required: 'Please add an Executor above',
      },
      asset_value_left_to_spouse: {
        max_currency:
          'The asset value left to spouse must not exceed gross asset value',
      },
      insurance_nominated_beneficiary: {
        required: 'Please add your nominated beneficiary',
      },
      total_cover: {
        max_value:
          'The total amount of cover must be between R50 000 and R200 000',
        min_value:
          'The total amount of cover must be between R50 000 and R200 000',
      },
      selected_testators: {
        is: 'At least one testator must be selected',
      },
    },
  },
});

function validateId(idNumber) {
  var correct = true;
  if (!regexpValidate(idNumber)) correct = false;
  if (!checkDateOfBirth(idNumber)) correct = false;
  if (!checkIdDigits(idNumber)) correct = false;

  return correct;
}

function validatePhone(phone) {
  // Accept either 10 digits or 10-15 digits starting with +
  const phoneNumberRegex = /^(?:\+?\d{10,15})$/;
  return phoneNumberRegex.test(phone);
}

function checkDateOfBirth(idNumber) {
  // Handle for dates that default to 1900s
  if (idNumber.substring(0, 2) > 20) {
    var tempDate = new Date(
      idNumber.substring(0, 2),
      idNumber.substring(2, 4) - 1,
      idNumber.substring(4, 6)
    );
  } else {
    var tempDate = new Date(
      '20' + idNumber.substring(0, 2),
      idNumber.substring(2, 4) - 1,
      idNumber.substring(4, 6)
    );
  }

  var id_date = tempDate.getDate();
  var id_month = tempDate.getMonth();
  var id_year = tempDate.getFullYear().toString().substring(2, 4);

  return (
    id_year == idNumber.substring(0, 2) &&
    id_month == idNumber.substring(2, 4) - 1 &&
    id_date == idNumber.substring(4, 6)
  );
}

function regexpValidate(idNumber) {
  var regexp = /^[0-9]{13}$/;
  return regexp.test(idNumber);
}

function checkIdDigits(idNumber) {
  var tempTotal = 0;
  var checkSum = 0;
  var multiplier = 1;
  var valid = true;
  for (var i = 0; i < 13; ++i) {
    tempTotal = parseInt(idNumber.charAt(i)) * multiplier;
    if (tempTotal > 9) {
      tempTotal =
        parseInt(tempTotal.toString().charAt(0)) +
        parseInt(tempTotal.toString().charAt(1));
    }
    checkSum = checkSum + tempTotal;
    multiplier = multiplier % 2 == 0 ? 1 : 2;
  }
  if (checkSum % 10 != 0) {
    valid = false;
  }
  return valid;
}

function isSixteenOrOlder(idNumber) {
  const birthdate = getBirthDateFromId(idNumber);
  if (!birthdate) return false;

  const age = calculateAge(birthdate);
  return age >= 16;
}

function getBirthDateFromId(idNumber) {
  let year = parseInt(idNumber.substring(0, 2), 10);
  const month = parseInt(idNumber.substring(2, 4), 10) - 1;
  const day = parseInt(idNumber.substring(4, 6), 10);

  if (year > 20) {
    year += 1900;
  } else {
    year += 2000;
  }

  const birthdate = new Date(year, month, day);

  if (isNaN(birthdate.getTime())) {
    return null;
  }

  return birthdate;
}

function calculateAge(birthdate) {
  const today = new Date();
  let age = today.getFullYear() - birthdate.getFullYear();
  const monthDiff = today.getMonth() - birthdate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthdate.getDate())
  ) {
    age--;
  }

  return age;
}
